import { FronteggContext } from '@frontegg/rest-api';
import { io } from 'socket.io-client';
import { analytics, logger } from 'src/services';
let audioContext, socket, input, micStream, audioWorkletNode;

let url = import.meta.env.VITE_AMBIENT_API_BASE_URL + '/listen';

// Add this near the top with other let declarations
let lastTranscriptTime = null;
const TRANSCRIPT_TIMEOUT_MS = 15000; // 15 seconds without transcription before showing error
let isTimeoutErrorShown = false; // Add this flag to track error state
let isOnOpenInProgress = false; // Add this flag to prevent concurrent onOpen calls

export const SAMPLE_RATE = 16000;

export async function doStream({
  encounterId,
  encounterPhase,
  setMicLevel,
  setLiveTranscript,
  setLivePartialTranscript,
  selectedMicrophoneId,
  successCallback,
  failureCallback,
  setDictation = () => {},
  audioContextOptions = {},
  isDictate = false,
  transcriptTimeoutCallback,
  user,
}) {
  let connectionEstablished = false;

  // Maximum number of times to attempt to reconnect if connection to deepgram/assemblyAI inside our socket.io server is lost.
  // Note that this is different from the "reconnectionAttempts" option, which is the number of times socketio automatically
  // attempts to reconnect due to network issues etc.
  const max_manual_reconnection_attempts = 5;
  const reconnection_window_ms = 5 * 60 * 1000; // 5 minutes in milliseconds
  let manual_reconnection_attempts = 0;
  let first_reconnection_attempt_time = null;

  // Clean up any existing resources before starting new stream
  endStream();

  // Get microphone stream and detect sample rate first
  // let micSampleRate = SAMPLE_RATE; // Default fallback
  try {
    const stream = await navigator.mediaDevices
      .getUserMedia({
        audio: selectedMicrophoneId ? { deviceId: selectedMicrophoneId } : true,
      })
      .catch(() => {
        return navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      });

    // // Detect the microphone's sample rate
    // try {
    //   micSampleRate = await detectMicrophoneSampleRate(stream);
    // } catch (error) {
    //   logger.warn(
    //     'Failed to detect microphone sample rate, using default:',
    //     error
    //   );
    // }

    // Store the stream for later use in onOpen
    micStream = stream;
  } catch (error) {
    failureCallback('MICROPHONE_SETUP_FAILED');
    return; // Exit early to prevent double callback execution
  }

  // Create AudioContext with the correct sample rate from the start
  audioContext = new (window.AudioContext || window.webkitAudioContext)({
    latencyHint: 'interactive',
    ...(audioContextOptions || {}),
  });
  audioContext.onstatechange = () => {
    const state = audioContext.state;
    if (state === 'suspended') {
      analytics.track(`User's Microphone Context Is Suspended`, {
        email: user?.email,
      });
      failureCallback('AUDIO_CONTEXT_SUSPENDED');
    }
  };
  socket = io(url, {
    path: '/socket.io/',
    withCredentials: true,
    query: {
      encounter_id: encounterId,
      encounter_phase: encounterPhase,
      sample_rate: audioContext.sampleRate,
      is_dictate: isDictate,
    },
    extraHeaders: {
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    },
    reconnection: true, // Enable automatic reconnection
    reconnectionAttempts: 12, // Retry for about 1 minute.
    reconnectionDelay: 5000, // Fixed delay for reconnection (ms)
    reconnectionDelayMax: 5000, // Maximum delay between reconnections (ms)
    forceNew: true,
  });

  // Add timeout checker
  const checkTranscriptTimeout = () => {
    if (
      lastTranscriptTime &&
      Date.now() - lastTranscriptTime > TRANSCRIPT_TIMEOUT_MS
    ) {
      if (!isTimeoutErrorShown) {
        isTimeoutErrorShown = true;
        transcriptTimeoutCallback('NO_TRANSCRIPTION_RECEIVED');
      }
    }
    setTimeout(checkTranscriptTimeout, 1000); // Check every second
  };

  socket.on('connected', () => {
    connectionEstablished = true;
    if (!window.isMicOn && !isOnOpenInProgress) {
      isOnOpenInProgress = true;
      onOpen(
        setMicLevel,
        selectedMicrophoneId,
        successCallback,
        failureCallback
      );
      lastTranscriptTime = Date.now();
      checkTranscriptTimeout();
    }
  });

  socket.on('transcript', (data) => {
    if (data.length > 0) {
      lastTranscriptTime = Date.now();
      if (isTimeoutErrorShown) {
        isTimeoutErrorShown = false;
        transcriptTimeoutCallback('ENABLED_TRANSCRIPTION_RECEIVED');
        successCallback(); // Use this to clear the error state
      }
    }
    if (isDictate) {
      setDictation(data);
    } else {
      setLiveTranscript(data);
    }
  });

  socket.on('partial_transcript', (data) => {
    if (data.length > 0) {
      lastTranscriptTime = Date.now();
      if (isTimeoutErrorShown) {
        isTimeoutErrorShown = false;
        transcriptTimeoutCallback('ENABLED_PARTIAL_TRANSCRIPTION_RECEIVED');
        successCallback(); // Use this to clear the error state
      }
    }
    if (!isDictate) {
      setLivePartialTranscript(data);
    }
  });

  socket.on('disconnect', (reason) => {
    console.error(
      `Socket is not connected; not sending audio until reconnected. ${reason}`
    );

    if (reason === 'io server disconnect') {
      const now = Date.now();
      // Initialize the first attempt time if it hasn't been set
      if (!first_reconnection_attempt_time) {
        first_reconnection_attempt_time = now;
      }

      // Check if we're still within the time window
      if (now - first_reconnection_attempt_time <= reconnection_window_ms) {
        if (manual_reconnection_attempts < max_manual_reconnection_attempts) {
          manual_reconnection_attempts++;
          console.log(
            `Attempt #${manual_reconnection_attempts} - Reconnecting to server...`
          );
          socket.connect();
        } else {
          console.error(
            `Maximum socket.io reconnection attempts of ${max_manual_reconnection_attempts} reached within 5 minute window`
          );
          analytics.track(
            `Maximum socket.io reconnection attempts of ${max_manual_reconnection_attempts} reached`,
            { email: user?.email }
          );
          failureCallback('MAX_RECONNECTIONS_EXCEEDED');
        }
      } else {
        // Reset counters if we're outside the time window
        manual_reconnection_attempts = 1;
        first_reconnection_attempt_time = now;
        console.log('Reconnection window reset - Attempting to reconnect...');
        socket.connect();
      }
    }
  });

  socket.on('connect_error', (error) => {
    if (!connectionEstablished) {
      console.error('Failed to connect to server:', error);
      failureCallback('NETWORK_INIT_ERROR');
    }
  });

  socket.io.on('reconnect_failed', () => {
    console.error('Given up on reconnecting to server.');
    failureCallback('NETWORK_ERROR');
  });
}

export function endStream() {
  lastTranscriptTime = null;
  window.isMicOn = false;
  isOnOpenInProgress = false; // Reset the flag when ending stream

  if (audioWorkletNode) {
    audioWorkletNode.port.onmessage = null;
    audioWorkletNode.disconnect();
  }

  if (input) {
    input.disconnect();
  }

  if (micStream) {
    micStream.getTracks().forEach((track) => track.stop());
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
  }

  if (socket) {
    socket.close();
  }
}

/**
 * Handle audio data received from the AudioWorklet processor
 * @param {Object} data - Audio data from worklet
 * @param {Function} setMicLevel - Callback to set microphone level
 */
function handleAudioData(data, setMicLevel) {
  if (
    audioContext.state === 'suspended' ||
    audioContext.state === 'closed' ||
    !socket
  ) {
    console.error(
      'AudioContext is suspended or closed. State = ',
      audioContext.state
    );
    return;
  }

  if (socket.connected) {
    socket.emit(
      'audio',
      JSON.stringify({
        timestamp: data.timestamp,
        audio: data.audio,
      })
    );
    setMicLevel(data.micLevel);
  } else {
    setMicLevel('offline');
  }
}

async function onOpen(
  setMicLevel,
  selectedMicrophoneId,
  successCallback,
  failureCallback
) {
  try {
    // Use the stream that was already obtained in doStream
    if (!micStream) {
      throw new Error('Microphone stream not available');
    }

    // Load the AudioWorklet module
    await audioContext.audioWorklet.addModule('/audio-processor.js');

    await audioContext.resume();

    // Create AudioWorkletNode instead of ScriptProcessorNode
    audioWorkletNode = new AudioWorkletNode(audioContext, 'audio-processor');
    input = audioContext.createMediaStreamSource(micStream);

    // Set up message handler for audio data from worklet
    audioWorkletNode.port.onmessage = (event) => {
      if (event.data.type === 'audioData') {
        handleAudioData(event.data, setMicLevel);
      }
    };

    // Connect the audio graph
    input.connect(audioWorkletNode);
    audioWorkletNode.connect(audioContext.destination);

    await audioContext.resume();

    window.isMicOn = true;
    isOnOpenInProgress = false; // Reset the flag on success
    successCallback();
  } catch (error) {
    console.error('Error accessing microphone:', error);
    isOnOpenInProgress = false; // Reset the flag on failure
    failureCallback('MICROPHONE_SETUP_FAILED');
  }
}

/**
 * Detect the sample rate of a microphone stream
 * @param {MediaStream} stream - The microphone stream
 * @returns {Promise<number>} - The detected sample rate
 */
export async function detectMicrophoneSampleRate(stream) {
  return new Promise((resolve) => {
    try {
      // Get the audio track from the stream
      const audioTrack = stream.getAudioTracks()[0];

      if (!audioTrack) {
        logger.warn('No audio track found in stream');
        resolve(SAMPLE_RATE);
        return;
      }

      // Get the settings of the audio track to access native sample rate
      const settings = audioTrack.getSettings();
      const detectedSampleRate = settings.sampleRate;

      if (detectedSampleRate) {
        logger.log(`Detected microphone sample rate: ${detectedSampleRate}Hz`);
        resolve(detectedSampleRate);
      } else {
        logger.warn(
          'Sample rate not available in track settings, using default'
        );
        resolve(SAMPLE_RATE);
      }
    } catch (error) {
      logger.warn('Error detecting microphone sample rate:', error);
      resolve(SAMPLE_RATE);
    }
  });
}
